import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const BulkUpload = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (validTypes.includes(selectedFile.type) || selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setResult(null);
      } else {
        alert('Please select a valid CSV or Excel file');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${BACKEND_URL}/api/children/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setResult(data);
      setFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${BACKEND_URL}/api/children/bulk-upload/template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download template');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="bulk-upload-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="page-title">
            {t('bulkUpload.title')}
          </h1>
          <p className="text-lg text-[#4B5563]">
            {t('bulkUpload.subtitle')}
          </p>
        </div>

        {/* Instructions Card */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-6">
          <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('bulkUpload.instructions')}</h3>
          <ol className="list-decimal list-inside space-y-2 text-[#4B5563]">
            <li>{t('bulkUpload.instruction1')}</li>
            <li>{t('bulkUpload.instruction2')}</li>
            <li>{t('bulkUpload.instruction3')}</li>
            <li>{t('bulkUpload.instruction4')}</li>
          </ol>
          <p className="mt-4 text-sm text-[#4B5563] bg-blue-100 p-3 rounded-lg">
            {t('bulkUpload.templateInfo')}
          </p>
        </div>

        {/* Download Template */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-6">
          <h3 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('bulkUpload.step1')}</h3>
          <button
            onClick={handleDownloadTemplate}
            data-testid="download-template-button"
            className="flex items-center space-x-3 px-6 py-3 bg-[#10B981] text-white rounded-full font-bold hover:bg-[#059669] transition-all active:scale-95 shadow-md"
          >
            <Download size={20} />
            <span>{t('bulkUpload.downloadTemplate')}</span>
          </button>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-6">
          <h3 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('bulkUpload.step2')}</h3>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
              <FileSpreadsheet className="mx-auto text-[#9CA3AF] mb-4" size={48} />
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                data-testid="file-input"
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-block px-6 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all"
              >
                {t('bulkUpload.selectFile')}
              </label>
              {file && (
                <p className="mt-4 text-sm text-[#4B5563] font-semibold">
                  {t('bulkUpload.selectedFile')}: {file.name}
                </p>
              )}
              <p className="mt-2 text-sm text-[#9CA3AF]">
                {t('bulkUpload.supportedFormats')}
              </p>
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="upload-button"
              className="w-full flex items-center justify-center space-x-3 px-8 py-4 bg-[#F59E0B] text-black rounded-full text-xl font-bold hover:bg-[#D97706] transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={24} />
              <span>{uploading ? t('bulkUpload.uploading') : t('bulkUpload.uploadFile')}</span>
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm" data-testid="upload-results">
            <h3 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('bulkUpload.uploadResults')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-[#F3F4F6] rounded-xl">
                <p className="text-sm font-semibold text-[#4B5563] mb-1">{t('bulkUpload.total')}</p>
                <p className="text-3xl font-bold text-[#111827] font-['Nunito']">{result.total}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl border-2 border-green-200">
                <div className="flex items-center justify-center space-x-2 mb-1">
                  <CheckCircle className="text-green-600" size={20} />
                  <p className="text-sm font-semibold text-green-700">{t('bulkUpload.successful')}</p>
                </div>
                <p className="text-3xl font-bold text-green-600 font-['Nunito']">{result.successful}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-xl border-2 border-red-200">
                <div className="flex items-center justify-center space-x-2 mb-1">
                  <XCircle className="text-red-600" size={20} />
                  <p className="text-sm font-semibold text-red-700">{t('bulkUpload.failed')}</p>
                </div>
                <p className="text-3xl font-bold text-red-600 font-['Nunito']">{result.failed}</p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <AlertCircle className="text-red-600" size={20} />
                  <h4 className="font-bold text-red-700">{t('bulkUpload.errors')} ({t('bulkUpload.showingFirst')} {result.errors.length})</h4>
                </div>
                <div className="space-y-2">
                  {result.errors.map((error, idx) => (
                    <p key={idx} className="text-sm text-red-700">
                      {t('bulkUpload.row')} {error.row}: {error.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex space-x-4">
              <button
                onClick={() => {
                  setResult(null);
                  setFile(null);
                }}
                data-testid="upload-another-button"
                className="px-6 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all"
              >
                {t('bulkUpload.uploadAnother')}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
                className="px-6 py-3 bg-[#10B981] text-white rounded-full font-bold hover:bg-[#059669] transition-all"
              >
                {t('common.goToDashboard')}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default BulkUpload;
